import { useCallback } from 'react';
import { API, View } from '@gin-betting/core';
import filters, { Filters } from '@gin-betting/filters';
import { createStructuredSelector } from 'reselect';
import type { ArgsN } from 'tsargs';

import { usePureSelector } from 'hooks';
import type { State } from 'types';
import { CategorizerRowType } from '../../../constants';
import useCategorizerRowChecker from '../useCategorizerRowChecker';

const {
    selectors: { selectEntityByCategorizerItem },
} = filters;

interface Input {
    categorizerItem: API.CategorizerItem;
    selectedTournamentIds: string[];
    isOpened: boolean;
    onSportSelected: (categorizerItem: API.CategorizerItem) => void;
    onSportPreviewClick: (sportId: string, type: Filters.CheckGameType) => void;
    handleTournamentIdsChange: (
        tournamentsIds: string[],
        sportIdToRemove?: string
    ) => void;
    isSelected: boolean;
}

interface Output {
    sport: View.Sport | undefined;
    activeTournamentIds: string[];
    isAllItemsInSportChecked: boolean;
    isTournamentManuallyChecked: boolean;
    handleSportSelected: VoidFunction;
    handleSportPreviewClick: VoidFunction;
    handleTournamentsCheck: (tournamentIds: string[]) => void;
}

// TODO: add type to selectEntityByCategorizerItem selector in filter lib
const selectEntityByCategorizerItemTyped: (
    ...args: ArgsN<typeof selectEntityByCategorizerItem>
) => View.Sport | undefined = selectEntityByCategorizerItem;

const selectProps = createStructuredSelector({
    entity: selectEntityByCategorizerItemTyped,
});

const useCategorizerRow = ({
    categorizerItem,
    selectedTournamentIds,
    isOpened,
    onSportSelected,
    onSportPreviewClick,
    handleTournamentIdsChange,
    isSelected,
}: Input): Output => {
    const { entity } = usePureSelector((state: State) =>
        selectProps(state, { categorizerItem })
    );

    const {
        activeTournamentIds,
        isAllTournamentsChecked,
        isTournamentManuallyChecked,
        handleItemCheck,
    } = useCategorizerRowChecker({
        items: categorizerItem.children,
        topCategorizerItem: categorizerItem,
        selectedTournamentIds,
        handleTournamentIdsChange,
    });

    const handleTournamentsCheck = useCallback(
        (checkedTournamentIds: string[]) => {
            handleItemCheck(
                checkedTournamentIds,
                CategorizerRowType.Tournament
            );
        },
        [handleItemCheck]
    );

    const handleSportSelected = useCallback(() => {
        onSportSelected(categorizerItem);
    }, [categorizerItem, onSportSelected]);

    const handleSportPreviewClick = useCallback(() => {
        const { value } = categorizerItem;

        onSportPreviewClick(
            value,
            isOpened ? Filters.CheckGameType.Close : Filters.CheckGameType.Open
        );

        if ((isOpened && !isSelected) || (!isOpened && isSelected)) return;

        handleSportSelected();
    }, [
        onSportPreviewClick,
        isOpened,
        isSelected,
        categorizerItem,
        handleSportSelected,
    ]);

    return {
        sport: entity,
        activeTournamentIds,
        isAllItemsInSportChecked: isAllTournamentsChecked,
        isTournamentManuallyChecked,
        handleSportPreviewClick,
        handleSportSelected,
        handleTournamentsCheck,
    };
};

export default useCategorizerRow;
